/**
 * Shared utilities for AnalysisPanel section components.
 * Color helpers, shared UI primitives, data builders.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { scoreBand } from '@/lib/scoreBand';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { XMLAnalysisResult, ComponentScores } from '../../AnalysisPanel';
import { formatQualityScore } from '@/lib/format-quality-score';

// ---------------------------------------------------------------------------
// Re-export types used across section files
// ---------------------------------------------------------------------------

export type { XMLAnalysisResult, ComponentScores };

// ---------------------------------------------------------------------------
// Score color helpers
// ---------------------------------------------------------------------------

const TEXT_BY_BAND = {
  excellent: 'text-success',
  good: 'text-primary',
  fair: 'text-warning',
  poor: 'text-danger',
} as const;

const BG_BY_BAND = {
  excellent: 'bg-success',
  good: 'bg-primary',
  fair: 'bg-warning',
  poor: 'bg-danger',
} as const;

/** Tailwind text color class by score value */
export function scoreTextColor(score: number): string {
  return TEXT_BY_BAND[scoreBand(score)];
}

/** Tailwind bg color class by score value */
export function scoreBgColor(score: number): string {
  return BG_BY_BAND[scoreBand(score)];
}

// ---------------------------------------------------------------------------
// Sentiment helpers
// ---------------------------------------------------------------------------

export function sentimentLabel(
  value: number | undefined,
  t: (key: string) => string
): string {
  if (value === undefined || value === null) return '?';
  if (value >= 0.5) return t('records.analysisPanel.veryPositive');
  if (value >= 0.2) return t('records.analysisPanel.positive');
  if (value >= -0.2) return t('records.analysisPanel.neutral');
  if (value >= -0.5) return t('records.analysisPanel.negative');
  return t('records.analysisPanel.veryNegative');
}

export function sentimentColor(value: number | undefined): string {
  if (value === undefined || value === null) return 'text-fg-subtle';
  if (value >= 0.5) return 'text-success';
  if (value >= 0.2) return 'text-success';
  if (value >= -0.2) return 'text-fg-muted';
  if (value >= -0.5) return 'text-warning';
  return 'text-danger';
}

// ---------------------------------------------------------------------------
// BANT helpers
// ---------------------------------------------------------------------------

export interface BantItem {
  label: string;
  identified: boolean;
  tagLabel: string;
  score?: number;
  quote?: string;
}

export function buildBantRows(
  bant: NonNullable<
    NonNullable<XMLAnalysisResult['dialogue']>['needsQualification']
  >['bant'],
  t: (key: string) => string
): BantItem[] {
  if (!bant) return [];
  const rows: BantItem[] = [];
  if (bant.budget)
    rows.push({
      label: t('records.analysisPanel.bant.budget'),
      identified: bant.budget.identified,
      tagLabel: bant.budget.identified
        ? t('records.analysisPanel.bant.confirmed')
        : t('records.analysisPanel.bant.notIdentified'),
      score: bant.budget.score,
      quote: bant.budget.quote,
    });
  if (bant.authority)
    rows.push({
      label: t('records.analysisPanel.bant.authority'),
      identified: bant.authority.identified,
      tagLabel: bant.authority.identified
        ? t('records.analysisPanel.bant.decisionMaker')
        : t('records.analysisPanel.bant.notIdentified'),
      score: bant.authority.score,
      quote: bant.authority.quote,
    });
  if (bant.need)
    rows.push({
      label: t('records.analysisPanel.bant.need'),
      identified: bant.need.identified,
      tagLabel: bant.need.identified
        ? t('records.analysisPanel.bant.confirmed')
        : t('records.analysisPanel.bant.notIdentified'),
      score: bant.need.score,
      quote: bant.need.quote,
    });
  if (bant.timeline)
    rows.push({
      label: t('records.analysisPanel.bant.timeline'),
      identified: bant.timeline.identified,
      tagLabel: bant.timeline.identified
        ? t('records.analysisPanel.bant.confirmed')
        : t('records.analysisPanel.bant.noTimeline'),
      score: bant.timeline.score,
      quote: bant.timeline.quote,
    });
  return rows;
}

// ---------------------------------------------------------------------------
// A-F score map
// ---------------------------------------------------------------------------

export const SCORE_MAP: {
  key: keyof ComponentScores;
  code: string;
  labelKey: string;
}[] = [
  {
    key: 'greetingScore',
    code: 'A',
    labelKey: 'records.analysisPanel.scores.greeting',
  },
  {
    key: 'needsScore',
    code: 'B',
    labelKey: 'records.analysisPanel.scores.needsDiscovery',
  },
  {
    key: 'objectionScore',
    code: 'C',
    labelKey: 'records.analysisPanel.scores.objectionHandling',
  },
  {
    key: 'closingScore',
    code: 'D',
    labelKey: 'records.analysisPanel.scores.closingTechniques',
  },
  {
    key: 'politenessScore',
    code: 'E',
    labelKey: 'records.analysisPanel.scores.politeness',
  },
  {
    key: 'clarityScore',
    code: 'F',
    labelKey: 'records.analysisPanel.scores.clarity',
  },
];

// ---------------------------------------------------------------------------
// Quality score breakdown keys
// ---------------------------------------------------------------------------

export const BREAKDOWN_KEYS: {
  field: keyof NonNullable<
    NonNullable<XMLAnalysisResult['summary']>['qualityScoreBreakdown']
  >;
  labelKey: string;
}[] = [
  {
    field: 'dialogueTechnique',
    labelKey: 'records.analysisPanel.qualityScore.breakdown.dialogueTechnique',
  },
  {
    field: 'contentQuality',
    labelKey: 'records.analysisPanel.qualityScore.breakdown.contentQuality',
  },
  {
    field: 'emotionalIntelligence',
    labelKey: 'records.analysisPanel.qualityScore.breakdown.emotionalIntelligence',
  },
  {
    field: 'compliance',
    labelKey: 'records.analysisPanel.qualityScore.breakdown.compliance',
  },
  {
    field: 'speechQuality',
    labelKey: 'records.analysisPanel.qualityScore.breakdown.speechQuality',
  },
];

// ---------------------------------------------------------------------------
// Confidence level -> percent
// ---------------------------------------------------------------------------

/**
 * Maps the model's self-reported confidence bucket to the percent shown next
 * to the score breakdown (AnalysisPanel) and, mirrored, in the print report's
 * quality-score card. A single place for the 94/70/40 buckets — inlining this
 * ternary a second time for print is exactly the divergence risk SCORE_MAP
 * above exists to close (the metric-C bug happened because the mapping was
 * copied, not shared).
 */
export function confidenceLevelPercent(
  level: 'high' | 'medium' | 'low' | undefined
): number | undefined {
  if (level === 'high') return 94;
  if (level === 'medium') return 70;
  if (level === 'low') return 40;
  return undefined;
}

/**
 * The same bucket as a word in the interface language — the "Confidence" row
 * of the sale-probability card and, mirrored, of the print report (#1393: the
 * raw `high` used to reach ru/uk/es screens as is). `null` when there is no
 * level — the row is then left out, as the percent above is: only demo
 * analyses carry `meta.confidenceLevel` today, so a placeholder would sit on
 * every real one. Takes `string`, not the enum: a value the type does not know
 * is no level either, and is never printed raw.
 */
export function confidenceLevelLabel(
  level: string | undefined,
  t: (key: string, defaultValue: string) => string
): string | null {
  if (level === 'high') return t('records.analysisPanel.confidenceLevel.high', 'High');
  if (level === 'medium')
    return t('records.analysisPanel.confidenceLevel.medium', 'Medium');
  if (level === 'low') return t('records.analysisPanel.confidenceLevel.low', 'Low');
  return null;
}

// ---------------------------------------------------------------------------
// Shared UI primitives
// ---------------------------------------------------------------------------

export function SectionTitle({
  children,
  confidence,
}: {
  children: React.ReactNode;
  confidence?: number;
}) {
  return (
    <h3 className="text-xs font-medium text-fg-muted uppercase tracking-wider mb-3 border-l-[3px] border-l-primary pl-2 py-1 bg-primary-dim rounded-r-sm">
      {children}
      {confidence !== undefined && (
        <span className="text-micro text-success ml-1.5 normal-case tracking-normal">
          ● {confidence}%
        </span>
      )}
    </h3>
  );
}

export function Tag({
  variant = 'neutral',
  children,
  className: extra,
}: {
  variant?: 'neutral' | 'success' | 'danger' | 'warning';
  children: React.ReactNode;
  className?: string;
}) {
  const base = 'inline-flex px-2 py-0.5 rounded text-xs font-medium';
  const variants: Record<string, string> = {
    neutral: 'bg-bg-elevated text-fg-muted',
    success: 'bg-success-dim text-success-fg',
    danger: 'bg-danger-dim text-danger-fg',
    warning: 'bg-warning-dim text-warning-fg',
  };
  return <span className={cn(base, variants[variant], extra)}>{children}</span>;
}

// ---------------------------------------------------------------------------
// AnalysisSection — collapsible card shell (new "Категории" layout)
// ---------------------------------------------------------------------------

/**
 * Collapsible analysis card. Header: 3px primary accent bar + uppercase title +
 * optional right-aligned scannable `summary` value + rotating chevron. Body
 * collapses via the shared Radix Collapsible primitives (uncontrolled —
 * `defaultOpen`). Mirrors the Claude Design "Call Analysis Panel" template.
 */
export function AnalysisSection({
  title,
  summary,
  defaultOpen = false,
  children,
}: {
  title: React.ReactNode;
  /** Fully-styled at-a-glance value shown in the collapsed header (score/%/count). */
  summary?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Collapsible
      defaultOpen={defaultOpen}
      className="group overflow-hidden rounded-card border border-border bg-bg-elevated"
    >
      <CollapsibleTrigger
        // A section header row, not a form control: its 48px floor is the card
        // layout's, so it stays out of the control scale (pre-existing).
        className="flex min-h-12 w-full items-center gap-2.5 px-4 py-3 text-left transition-colors duration-150 hover:bg-bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <span className="h-4 w-[3px] shrink-0 rounded-sm bg-primary" aria-hidden="true" />
        <span className="text-xs font-semibold uppercase tracking-wide text-fg-secondary">
          {title}
        </span>
        {summary != null && <span className="ml-auto flex items-center">{summary}</span>}
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-fg-subtle transition-transform duration-150 group-data-[state=open]:rotate-180',
            summary == null && 'ml-auto'
          )}
          aria-hidden="true"
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <div className="px-4 pb-4 pt-0.5">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ---------------------------------------------------------------------------
// GroupDivider — semantic section group label (Оценка / Скрипт / Речь / …)
// ---------------------------------------------------------------------------

export function GroupDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 px-0.5 pt-2">
      <span className="font-mono text-micro font-semibold uppercase tracking-widest text-primary">
        {label}
      </span>
      <span className="h-px flex-1 bg-border" aria-hidden="true" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CategoryFilter — single-select pill bar to scope the panel to one group
// ---------------------------------------------------------------------------

export interface CategoryTab {
  /** Stable key — 'all' is reserved for the "show everything" pill. */
  key: string;
  label: string;
}

/**
 * Pill row that filters the analysis panel by category (Все / Оценка / Скрипт /
 * Речь / Инсайты). Single-select toggle buttons (`aria-pressed`); mirrors the
 * Claude Design "Категории" structure. The `all` pill is prepended by the panel.
 */
export function CategoryFilter({
  tabs,
  active,
  onChange,
}: {
  tabs: CategoryTab[];
  active: string;
  onChange: (key: string) => void;
}) {
  const { t } = useTranslation('records');
  return (
    <div
      className="flex flex-wrap gap-1.5 px-0.5"
      role="group"
      aria-label={t('records.analysisPanel.filterAriaLabel')}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(tab.key)}
            className={cn(
              'rounded-full border px-3 py-1.5 font-mono text-micro font-medium uppercase tracking-wider transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive
                ? 'border-primary bg-primary-dim text-primary-fg'
                : 'border-border bg-bg-surface text-fg-muted hover:bg-bg-elevated hover:text-fg-secondary'
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeroScore — overall quality score hero card
// ---------------------------------------------------------------------------

/**
 * "The CRM and alert score of this call was scored with the weights of the time
 * of analysis" (#874). Under the overall score, or on its own when the current
 * weights give the call no score at all — that is when the CRM number differs
 * most plainly.
 */
export function WeightedAtAnalysisNote({ className }: { className?: string }) {
  const { t } = useTranslation('records');
  return (
    <p
      data-testid="quality-weights-at-analysis"
      className={cn('text-xs text-fg-muted', className)}
    >
      {t('records.analysisPanel.qualityScore.weightedAtAnalysis')}
    </p>
  );
}

export function HeroScore({
  score,
  attentionBlock,
  scoredWithEarlierWeights,
  weighted,
}: {
  score: number;
  /**
   * The raised attention flag (<AttentionFlagBlock>), built once by the panel:
   * under the score here, on a card of its own when there is no score (#2020).
   */
  attentionBlock?: React.ReactNode;
  /**
   * The project's weights at the time of analysis would score this call
   * differently from the current ones (#874): the number sent to the CRM and the
   * alerts of this call is not the one above.
   */
  scoredWithEarlierWeights?: boolean;
  /** The score is a weighted average by the project's weights (#874). */
  weighted?: boolean;
}) {
  const { t } = useTranslation('records');
  return (
    <div className="rounded-card border border-border bg-bg-elevated p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-micro font-semibold uppercase tracking-widest text-fg-muted">
            {t('records.analysisPanel.qualityScore.overallScore')}
          </div>
          <div className="mt-1.5 flex items-baseline gap-1">
            <span
              className={cn(
                'font-metric text-4xl font-bold leading-none tracking-tight tabular-nums',
                scoreTextColor(score)
              )}
            >
              {formatQualityScore(score)}
            </span>
            <span className="font-mono text-sm text-fg-subtle">/100</span>
          </div>
        </div>
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-bg-surface">
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-[600ms]',
            scoreBgColor(score)
          )}
          style={{ width: `${score}%` }}
        />
      </div>
      {weighted && (
        <p data-testid="quality-weighted-average" className="mt-3 text-xs text-fg-muted">
          {t('records.analysisPanel.qualityScore.weightedAverage')}
        </p>
      )}
      {scoredWithEarlierWeights && (
        <WeightedAtAnalysisNote className={weighted ? 'mt-1' : 'mt-3'} />
      )}
      {attentionBlock && <div className="mt-3">{attentionBlock}</div>}
    </div>
  );
}
